import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import { getMessageProps, messageIdExists } from 'Store/reducers';

const ConnectedFormattedMessage = props => {
  const {
    containsHtml,
    convertToParagraphs,
    formattingCallback,
    allowNullResponse,
    ...passedProps
  } = props;

  if (allowNullResponse && !props.defaultMessage) return null;
  if (containsHtml) return <FormattedHTMLMessage {...passedProps} />;

  // This takes an array of renderable nodes, groups them into
  // paragraphs by splitting on two line breaks, then returns
  // the groups rendered as new paragraph nodes.
  const makeParagraphs = nodes => {
    const paragraphNodes = [[]];
    let index = 0;
    nodes.forEach(node => {
      if (typeof node !== 'string') {
        paragraphNodes[index].push(node);
      } else {
        const moreNodes = node.split('\n\n');
        if (moreNodes.length === 1) return paragraphNodes[index].push(node);
        moreNodes.forEach((interiorNode, i) => {
          paragraphNodes[index].push(interiorNode);
          paragraphNodes.push([]);
          if (i + 1 < moreNodes.length) index += 1;
        });
      }
      return false;
    });

    return (
      <React.Fragment>
        {paragraphNodes.map((node, i) => {
          if (node.length === 0) return null;
          return (
            // eslint-disable-next-line react/no-array-index-key
            <p className="TextBlock__paragraph" key={`graf-${i}`}>
              {node}
            </p>
          );
        })}
      </React.Fragment>
    );
  };

  const processNodes = nodes => {
    // Since we’re dealing with nodes and not just strings,
    // we can only do custom formatting if we know there is _just_ a string.
    // Also, we are going to punt on the formattingCallback when
    // there are multiple nodes. Just don’t do it.
    if (
      nodes.length === 1 &&
      typeof nodes[0] === 'string' &&
      formattingCallback
    ) {
      return formattingCallback(nodes[0]);
    }
    if (convertToParagraphs) {
      return makeParagraphs(nodes);
    }
    // Even though the library claims they avoid 'missing key' warnings,
    // they still come up if one of the nodes is something other
    // than a string. And so, keys.
    if (nodes.length > 1)
      return nodes.map((node, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`fm-node-${props.id}-${i}`}>{node}</React.Fragment>
      ));

    return nodes;
  };

  // Normally, we use the render prop because it lets us skip the
  // wrapper element.
  return (
    <FormattedMessage {...passedProps}>
      {(...nodes) => {
        return <React.Fragment>{processNodes(nodes)}</React.Fragment>;
      }}
    </FormattedMessage>
  );
};

const mapStateToProps = (state, { id }) => {
  if (!messageIdExists(state, id)) return {};

  return getMessageProps(state, id);
};

ConnectedFormattedMessage.propTypes = {
  /** This is the ID used to retrieve the localized string */
  id: PropTypes.string.isRequired,
  /** Values for strings that have variables */
  values: PropTypes.object,
  /**
   * We don’t use this, but react-intl provides it as a way
   * to explain the context of the string.
   */
  description: PropTypes.string,
  /**
   * If there is no translation, fall back to this. We pull it
   * from the en-US "translations".
   */
  defaultMessage: PropTypes.string,
  /** If the string has HTML, we need to use a different component */
  containsHtml: PropTypes.bool,
  /**
   * This takes the string provided and splits the text every two
   * line breaks.
   */
  convertToParagraphs: PropTypes.bool,
  /**
   * This function takes the retrieved text and modifies it. For instance,
   * it could take a string separated by line breaks and turn it into
   * a list.
   */
  formattingCallback: PropTypes.func,
  /** If there is no matching ID in the redux store, return null */
  allowNullResponse: PropTypes.bool,
};

export default connect(mapStateToProps)(ConnectedFormattedMessage);
